import { defineStore } from 'pinia';
import {
  login as loginService,
  logout as logoutService,
  validateToken,
  LoginRequest,
  LoginUser,
} from '@/service/auth';
import {
  resolveAccessibleShopIds,
  resolveDefaultShopId,
  resolveMenuPermissions,
} from '@/constants/auth';
import { cacheSelectedShop, readSelectedShop } from '@/utils/selectedShop';

const TOKEN_KEY = 'staff_auth_token';
const LEGACY_TOKEN_KEY = 'Token';
const USER_KEY = 'staff_auth_user';
const EXPIRES_AT_KEY = 'staff_auth_expires_at';

interface AuthState {
  token: string;
  user: LoginUser | null;
  expiresAt: number;
  validated: boolean;
  selectedShopId?: number;
}

function resolveSelectedShopId(roles: readonly string[]): number | undefined {
  const cachedShopId = readSelectedShop();
  const accessibleShopIds = resolveAccessibleShopIds(roles);
  const selectedShopId =
    cachedShopId && (!accessibleShopIds || accessibleShopIds.includes(cachedShopId))
      ? cachedShopId
      : resolveDefaultShopId(roles);
  cacheSelectedShop(selectedShopId);
  return selectedShopId;
}

function parseUser(raw: string | null): LoginUser | null {
  if (!raw) return null;

  try {
    const user = JSON.parse(raw) as Partial<LoginUser>;
    if (typeof user.username !== 'string' || typeof user.displayName !== 'string') return null;
    return {
      username: user.username,
      displayName: user.displayName,
      roles: Array.isArray(user.roles)
        ? user.roles.filter((role): role is string => typeof role === 'string')
        : [],
    };
  } catch {
    return null;
  }
}

function parseExpiresAt(raw: string | null): number {
  if (!raw) return 0;
  const timestamp = Number(raw);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function clearStoredAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
  localStorage.removeItem('employeeId');
  localStorage.removeItem('staff_auth_permissions');
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(EXPIRES_AT_KEY);
  sessionStorage.removeItem('employeeId');
  sessionStorage.removeItem('staff_auth_permissions');
}

function persistAuth(token: string, user: LoginUser, expiresAt: number): void {
  const userJson = JSON.stringify(user);
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, userJson);
  localStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(USER_KEY, userJson);
  sessionStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  localStorage.removeItem('employeeId');
  sessionStorage.removeItem('employeeId');
}

function getInitialState(): AuthState {
  const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
  const expiresAt = parseExpiresAt(
    localStorage.getItem(EXPIRES_AT_KEY) || sessionStorage.getItem(EXPIRES_AT_KEY),
  );

  if (!token || !expiresAt || expiresAt <= Date.now()) {
    clearStoredAuth();
    return { token: '', user: null, expiresAt: 0, validated: false, selectedShopId: undefined };
  }

  const user = parseUser(localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY));
  return {
    token,
    user,
    expiresAt,
    validated: false,
    selectedShopId: resolveSelectedShopId(user?.roles || []),
  };
}

export function getStoredToken(): string {
  const expiresAt = parseExpiresAt(
    localStorage.getItem(EXPIRES_AT_KEY) || sessionStorage.getItem(EXPIRES_AT_KEY),
  );
  if (!expiresAt || expiresAt <= Date.now()) return '';
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => getInitialState(),
  getters: {
    isLoggedIn: (state): boolean => Boolean(state.token) && state.expiresAt > Date.now(),
    menuPermissions: (state): string[] =>
      resolveMenuPermissions(state.user?.roles || [], state.selectedShopId),
    hasSystemAccess(): boolean {
      return this.menuPermissions.length > 0;
    },
    hasMenuPermission(): (menuName: string) => boolean {
      const permissions = new Set(this.menuPermissions);
      return (menuName: string): boolean => permissions.has(menuName);
    },
  },
  actions: {
    clearSession(): void {
      this.token = '';
      this.user = null;
      this.expiresAt = 0;
      this.validated = false;
      this.selectedShopId = undefined;
      clearStoredAuth();
    },
    async ensureValidSession(): Promise<boolean> {
      if (!this.token || this.expiresAt <= Date.now()) {
        this.clearSession();
        return false;
      }
      if (this.validated) return true;

      try {
        this.user = await validateToken(this.token);
        this.selectedShopId = resolveSelectedShopId(this.user.roles);
        this.validated = true;
        persistAuth(this.token, this.user, this.expiresAt);
        return true;
      } catch (error) {
        console.warn('Keycloak Token 校验失败', error);
        this.clearSession();
        return false;
      }
    },
    async login(payload: LoginRequest): Promise<void> {
      const result = await loginService(payload);
      this.token = result.token;
      this.user = result.user;
      this.selectedShopId = resolveSelectedShopId(result.user.roles);
      this.expiresAt = result.expiresAt;
      this.validated = true;
      persistAuth(result.token, result.user, result.expiresAt);
    },
    async logout(): Promise<void> {
      const token = this.token;
      this.clearSession();
      if (!token) return;

      try {
        await logoutService(token);
      } catch (error) {
        console.warn('Keycloak 退出接口调用失败', error);
      }
    },
    selectShop(shopId: number): void {
      this.selectedShopId = shopId;
      cacheSelectedShop(shopId);
    },
  },
});
